import { useCallback, useEffect, useRef, useState } from "react";
import type { PlaySoundAction } from "@/lib/audio/use-play-sound";
import type { SyllableTiming } from "@/lib/data/words";

function useSyllablePlaybackTracking(timings: readonly SyllableTiming[]) {
  const playbackFrameRef = useRef<number | null>(null);
  const activeSyllableRef = useRef<number | null>(null);
  const [activeSyllable, setActiveSyllable] = useState<number | null>(null);

  const updateActiveSyllable = useCallback((next: number | null) => {
    if (activeSyllableRef.current === next) return;
    activeSyllableRef.current = next;
    setActiveSyllable(next);
  }, []);

  const stopPlaybackTracking = useCallback(() => {
    if (playbackFrameRef.current !== null) {
      window.cancelAnimationFrame(playbackFrameRef.current);
      playbackFrameRef.current = null;
    }
    updateActiveSyllable(null);
  }, [updateActiveSyllable]);

  const startPlaybackTracking = useCallback(
    (audio: HTMLAudioElement) => {
      stopPlaybackTracking();

      const syncHighlightToPlayback = () => {
        if (audio.paused || audio.ended) {
          playbackFrameRef.current = null;
          updateActiveSyllable(null);
          return;
        }

        const currentTime = audio.currentTime;
        const index = timings.findIndex(
          ({ startSeconds, durationSeconds }) =>
            currentTime >= startSeconds &&
            currentTime < startSeconds + durationSeconds,
        );
        updateActiveSyllable(index === -1 ? null : index);

        playbackFrameRef.current = window.requestAnimationFrame(
          syncHighlightToPlayback,
        );
      };

      syncHighlightToPlayback();
    },
    [stopPlaybackTracking, timings, updateActiveSyllable],
  );

  useEffect(() => () => stopPlaybackTracking(), [stopPlaybackTracking]);

  return { activeSyllable, startPlaybackTracking, stopPlaybackTracking };
}

export function useSyllableTiming(
  playWord: PlaySoundAction,
  timings: readonly SyllableTiming[],
) {
  const pendingPlaybackRef = useRef<number | null>(null);
  const [isPlaybackPending, setIsPlaybackPending] = useState(false);
  const { activeSyllable, startPlaybackTracking, stopPlaybackTracking } =
    useSyllablePlaybackTracking(timings);

  const clearTiming = useCallback(() => {
    stopPlaybackTracking();
    if (pendingPlaybackRef.current !== null) {
      window.clearTimeout(pendingPlaybackRef.current);
      pendingPlaybackRef.current = null;
    }
    setIsPlaybackPending(false);
  }, [stopPlaybackTracking]);

  useEffect(() => () => clearTiming(), [clearTiming]);

  const playTimedWord = useCallback(
    (delayMs = 0) => {
      clearTiming();

      const startPlayback = () => {
        pendingPlaybackRef.current = null;
        setIsPlaybackPending(false);
        playWord(stopPlaybackTracking, startPlaybackTracking);
      };

      if (delayMs === 0) {
        startPlayback();
        return;
      }

      setIsPlaybackPending(true);
      pendingPlaybackRef.current = window.setTimeout(startPlayback, delayMs);
    },
    [clearTiming, playWord, startPlaybackTracking, stopPlaybackTracking],
  );

  return { activeSyllable, clearTiming, isPlaybackPending, playTimedWord };
}
